import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, ArrowLeftRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin-only dialog to execute an investor swap on behalf of the seller.
 * Calls POST /api/admin/investor-swaps/on-behalf.
 *
 * Props:
 *   - trigger: ReactNode (button/element to open the dialog)
 *   - onCreated: () => void (parent refresh callback)
 */
export default function CreateSwapOnBehalfDialog({ trigger, onCreated }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [tranches, setTranches] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [tranchesLoaded, setTranchesLoaded] = useState(false);

  const [tranchePicker, setTranchePicker] = useState('');
  const [buyerId, setBuyerId] = useState('');
  const [buyerRate, setBuyerRate] = useState('');
  const [note, setNote] = useState('');
  const [tranchesQuery, setTranchesQuery] = useState('');
  const [buyersQuery, setBuyersQuery] = useState('');

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, iRes] = await Promise.all([
        axios.get(`${API}/api/admin/investor-swaps/eligible-tranches`, authHeader),
        axios.get(`${API}/api/admin/investors?status=active&allocatable=true&limit=1000`, authHeader),
      ]);
      setTranches(tRes.data.items || []);
      const investorsPayload = iRes.data.investors || iRes.data.items || iRes.data || [];
      setInvestors(Array.isArray(investorsPayload) ? investorsPayload : []);
      setTranchesLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load swap options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !tranchesLoaded) loadData();
  }, [open, tranchesLoaded]);

  const reset = () => {
    setTranchePicker('');
    setBuyerId('');
    setBuyerRate('');
    setNote('');
    setTranchesQuery('');
    setBuyersQuery('');
  };

  const selectedTranche = useMemo(
    () => tranches.find(t => `${t.loan_id}|${t.seller_rp_code}` === tranchePicker) || null,
    [tranchePicker, tranches]
  );

  const filteredTranches = useMemo(() => {
    const q = tranchesQuery.trim().toLowerCase();
    if (!q) return tranches.slice(0, 200);
    return tranches.filter(t =>
      (t.loan_id || '').toLowerCase().includes(q) ||
      (t.seller_rp_code || '').toLowerCase().includes(q) ||
      (t.seller_name || '').toLowerCase().includes(q) ||
      (t.client_name || '').toLowerCase().includes(q)
    ).slice(0, 200);
  }, [tranchesQuery, tranches]);

  const filteredInvestors = useMemo(() => {
    const q = buyersQuery.trim().toLowerCase();
    const list = investors.filter(u => u.id !== selectedTranche?.seller_user_id);
    if (!q) return list.slice(0, 200);
    return list.filter(u =>
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.risk_participant_code || '').toLowerCase().includes(q)
    ).slice(0, 200);
  }, [buyersQuery, investors, selectedTranche]);

  const buyerRateNum = parseFloat(buyerRate);
  const carryPreview = selectedTranche && !isNaN(buyerRateNum) && buyerRateNum > 0
    ? Math.max(0, Number(((selectedTranche.seller_rate - buyerRateNum) || 0).toFixed(4)))
    : null;

  const rateInvalid = selectedTranche && !isNaN(buyerRateNum) && (buyerRateNum <= 0 || buyerRateNum > selectedTranche.seller_rate);

  const canSubmit = selectedTranche && buyerId && !isNaN(buyerRateNum) && !rateInvalid && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/admin/investor-swaps/on-behalf`, {
        loan_id: selectedTranche.loan_id,
        seller_rp_code: selectedTranche.seller_rp_code,
        buyer_user_id: buyerId,
        buyer_rate: buyerRateNum,
        note: note || null,
      }, authHeader);
      const { new_loan_id, carry_rate, accrued_price } = res.data || {};
      toast.success(`Swap executed. New tranche ${new_loan_id} @ ${buyerRateNum}% for ${zar(accrued_price)} (carry ${carry_rate || 0}%).`);
      setOpen(false);
      reset();
      // Force a fresh reload next open so the retired tranche disappears
      setTranchesLoaded(false);
      if (typeof onCreated === 'function') onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to execute swap');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)} data-testid="create-swap-on-behalf-trigger">{trigger}</div>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="h-5 w-5" /> Create swap on behalf of</DialogTitle>
          <DialogDescription>
            Execute an investor-to-investor swap for a specific loan tranche. The seller keeps the spread as carry when the buyer&apos;s rate is lower.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#6C757D]" /></div>
        ) : (
          <div className="space-y-4">
            {/* Seller tranche picker */}
            <div className="space-y-2">
              <Label>Seller loan tranche</Label>
              <Input
                placeholder="Search by loan id, RP code, seller or client"
                value={tranchesQuery}
                onChange={(e) => setTranchesQuery(e.target.value)}
                data-testid="swap-tranches-search"
              />
              <div className="max-h-52 overflow-auto rounded-md border" data-testid="swap-tranches-list">
                {filteredTranches.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#6C757D]">No matching tranches</div>
                ) : filteredTranches.map(t => {
                  const key = `${t.loan_id}|${t.seller_rp_code}`;
                  const active = tranchePicker === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTranchePicker(key)}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 text-sm ${active ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                      data-testid={`swap-tranche-${t.loan_id}-${t.seller_rp_code}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-mono">{t.loan_id}</span>
                        <span className="font-mono text-xs text-[#6C757D]">{t.seller_rp_code}</span>
                      </div>
                      <div className="flex justify-between gap-2 text-xs text-[#6C757D]">
                        <span>{t.seller_name} · {t.client_name || '—'}</span>
                        <span>{Number(t.seller_rate || 0).toFixed(2)}% · {zar(t.accrued_today)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedTranche && (
                <div className="text-xs text-[#6C757D]" data-testid="swap-tranche-summary">
                  Selected: <span className="font-mono">{selectedTranche.loan_id}</span> at <b>{Number(selectedTranche.seller_rate || 0).toFixed(2)}%</b> · accrued today {zar(selectedTranche.accrued_today)}
                </div>
              )}
            </div>

            {/* Buyer investor picker */}
            <div className="space-y-2">
              <Label>Buyer investor</Label>
              <Input
                placeholder="Search by name, email, or RP code"
                value={buyersQuery}
                onChange={(e) => setBuyersQuery(e.target.value)}
                data-testid="swap-buyers-search"
              />
              <div className="max-h-52 overflow-auto rounded-md border" data-testid="swap-buyers-list">
                {filteredInvestors.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[#6C757D]">No matching investors</div>
                ) : filteredInvestors.map(u => {
                  const active = buyerId === u.id;
                  const rp = u.risk_participant_code || u.rp_code || '—';
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setBuyerId(u.id)}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 text-sm ${active ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                      data-testid={`swap-buyer-${u.id}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span>{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</span>
                        <span className="font-mono text-xs text-[#6C757D]">{rp}</span>
                      </div>
                      <div className="text-xs text-[#6C757D]">{u.email}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Buyer rate + note */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Buyer rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 12.50"
                  value={buyerRate}
                  onChange={(e) => setBuyerRate(e.target.value)}
                  data-testid="swap-buyer-rate"
                />
                {selectedTranche && (
                  <div className="text-[11px] text-[#6C757D]">
                    Must be ≤ seller&apos;s rate ({Number(selectedTranche.seller_rate || 0).toFixed(2)}%). Equal = 0 carry.
                  </div>
                )}
                {rateInvalid && (
                  <div className="text-[11px] text-red-600" data-testid="swap-rate-error">
                    Buyer rate must be between 0 and {Number(selectedTranche?.seller_rate || 0).toFixed(2)}%.
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Carry rate</Label>
                <div className="h-10 px-3 flex items-center rounded-md border bg-gray-50 font-mono text-sm" data-testid="swap-carry-preview">
                  {carryPreview !== null ? `${carryPreview}%` : '—'}
                </div>
                <div className="text-[11px] text-[#6C757D]">Seller keeps this spread until the loan settles.</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                placeholder="Any context for the audit log"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="swap-note"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting} data-testid="swap-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
            data-testid="swap-submit"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Executing…</> : 'Execute swap'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
